/**
 * M7 S34: the claim panel's model (spec §2.3, §3.1, §17). Pure; the host carries out the effects.
 *
 * - **Draft:** a claim can be drafted when the next tier's attempt is open (`claims.ts claimUnlocked`); the Logs
 *   screen then shows a draft row on *The case*.
 * - **Typed slots:** each slot offers the pool words of its own type plus the template's decoys (a decoy may sit in
 *   any slot), in id order. A slot whose type has an answer in the pool always offers at least two same-type words
 *   (`wordPool` pads), so a slot never gives its answer away.
 * - **Frame and fallback:** the claim is put to its framed recipient (`claimPanel.json frames`); when the recipient's
 *   dock is refused (`contacts.ts contactFor`, `claim<n>`) it is filed on comms with the fallback speaker.
 * - **Retry rule:** after an attempt, the next one needs a newly read log or the next faction day, at most
 *   `attemptsPerDay` a day. While it waits, the panel says only "Read something new, or sleep on it.": never a
 *   count, never how many words are wrong. A wrong claim shows only `claims.json feedback.wrong`.
 * - **Keyboard and pad:** up and down move between the slots and *Send*; left and right change the focused slot's
 *   word; activate on *Send* sends, on a slot steps its word; back returns to the case.
 */
import type { Career } from '../../../career/types'
import { attemptRefusal, claimUnlocked, nextClaimTier, templateFor, wordPool, wordText, type ClaimData, type ClaimRefusal } from '../../../story/claims'
import type { NavInput } from '../../strategic/mount'
import { z } from 'zod'
import configJson from '../claimPanel.json'

const text = z.string().min(1)

export const claimPanelConfigSchema = z.strictObject({
  comment: z.string(),
  claim: z.strictObject({
    title: text,
    draftRow: text,
    frames: z.record(z.string(), text),
    to: text,
    fallback: text,
    slotHint: text,
    empty: text,
    send: text,
    sent: text,
    retry: text,
    back: text,
  }),
  bundle: z.strictObject({
    title: text,
    intro: text,
    original: text,
    copy: text,
    picked: text,
    full: text,
    unavailable: text,
    oppositionHeading: text,
    noGaps: text,
    handIn: text,
    back: text,
    opposition: z.strictObject({ order: text, knowledge: text, coverup: text, complicity: text }),
  }),
})
export type ClaimPanelConfig = z.output<typeof claimPanelConfigSchema>

let cfgCache: ClaimPanelConfig | null = null

export function loadClaimPanelConfig(raw: unknown = configJson): ClaimPanelConfig {
  if (raw === configJson && cfgCache !== null) return cfgCache
  const cfg = claimPanelConfigSchema.parse(raw)
  if (raw === configJson) cfgCache = cfg
  return cfg
}

/** Who takes the claim (from the story link's `contactFor`). */
export interface ClaimRecipient {
  /** The speaker's display name. */
  name: string
  /** True when the recipient's dock is refused and the claim goes by comms. */
  fallback: boolean
}

export interface ClaimInputs {
  career: Career
  data: ClaimData
  config: ClaimPanelConfig
  /** The worker's faction day (the retry rule's day). */
  day: number
  recipient: ClaimRecipient
}

export interface WordChoice {
  word: string
  text: string
}

export interface ClaimSlotView {
  index: number
  type: string
  choices: readonly WordChoice[]
}

/** The sentence as pieces: fixed text, or a slot by index. */
export type SentencePiece = { kind: 'text'; text: string } | { kind: 'slot'; index: number }

export interface ClaimView {
  tier: number
  title: string
  frame: string
  to: string
  /** The fallback line, or null when the framed recipient takes it. */
  fallback: string | null
  sentence: readonly SentencePiece[]
  slots: readonly ClaimSlotView[]
  /** The retry rule's line while an attempt waits; null when one may be made. */
  retry: string | null
}

export interface ClaimState {
  tier: number
  /** One word id (or null) per slot. */
  picks: readonly (string | null)[]
  /** A slot index, or `slots.length` for *Send*. */
  focus: number
  /** The answer shown after a send: `feedback.wrong`, or the sent line. */
  feedback: string | null
}

export type ClaimAction =
  | { type: 'nav'; input: NavInput }
  | { type: 'pick'; slot: number; word: string }
  | { type: 'focus'; index: number }
  | { type: 'send' }
  | { type: 'result'; correct: boolean }

export type ClaimEffect = { kind: 'submit'; tier: number; words: string[] } | { kind: 'close' }

export interface ClaimStep {
  state: ClaimState
  effect: ClaimEffect | null
}

/** The tier a claim can be drafted for now, or null. */
export function draftTier(career: Career, data: ClaimData): number | null {
  const tier = nextClaimTier(career)
  return tier !== null && claimUnlocked(career, tier, data) ? tier : null
}

/** The words a slot of `type` offers: same-type pool words, then the decoys of other types (id order each). */
export function slotChoices(pool: readonly string[], type: string, tier: number, data: ClaimData): WordChoice[] {
  const t = templateFor(tier, data)
  const decoys = new Set(t?.decoys.map((d) => d.word) ?? [])
  const typed = pool.filter((w) => data.claims.words[w]?.type === type)
  const others = pool.filter((w) => decoys.has(w) && !typed.includes(w))
  return [...typed, ...others].map((w) => ({ word: w, text: wordText(w, data) }))
}

const SLOT = /\{(\d+)\}/g

function sentencePieces(sentence: string): SentencePiece[] {
  const out: SentencePiece[] = []
  let at = 0
  for (const m of sentence.matchAll(SLOT)) {
    const i = m.index
    if (i > at) out.push({ kind: 'text', text: sentence.slice(at, i) })
    out.push({ kind: 'slot', index: Number(m[1]) })
    at = i + m[0].length
  }
  if (at < sentence.length) out.push({ kind: 'text', text: sentence.slice(at) })
  return out
}

/** Refusals the panel shows as the retry rule (the rest close the draft). */
const WAITS: ReadonlySet<ClaimRefusal> = new Set<ClaimRefusal>(['dayLimit', 'needNewLog'])

/** The claim panel's view, or null when no claim can be drafted now. */
export function buildClaimView(i: ClaimInputs): ClaimView | null {
  const tier = draftTier(i.career, i.data)
  if (tier === null) return null
  const t = templateFor(tier, i.data)
  if (t === undefined) return null
  const c = i.config.claim
  const pool = wordPool(i.career, tier, i.data)
  const refusal = attemptRefusal(i.career, tier, i.day, i.data)
  return {
    tier,
    title: c.title,
    frame: c.frames[String(tier)] ?? c.title,
    to: i.recipient.name,
    fallback: i.recipient.fallback ? c.fallback.replace('{speaker}', i.recipient.name) : null,
    sentence: sentencePieces(t.sentence),
    slots: t.slots.map((s, index) => ({ index, type: s.type, choices: slotChoices(pool, s.type, tier, i.data) })),
    retry: refusal !== null && WAITS.has(refusal) ? c.retry : null,
  }
}

export function createClaimState(view: ClaimView): ClaimState {
  return { tier: view.tier, picks: view.slots.map(() => null), focus: 0, feedback: null }
}

/** `s` still valid for `view` (a new tier starts a fresh draft; picks no longer offered are dropped). */
export function clampClaim(s: ClaimState, view: ClaimView): ClaimState {
  if (s.tier !== view.tier || s.picks.length !== view.slots.length) return createClaimState(view)
  const picks = s.picks.map((p, i) => (p !== null && view.slots[i]?.choices.some((c) => c.word === p) === true ? p : null))
  const focus = Math.min(Math.max(0, s.focus), view.slots.length)
  if (focus === s.focus && picks.every((p, i) => p === s.picks[i])) return s
  return { ...s, picks, focus }
}

/** Every slot has a word and no retry is waiting. */
export function canSend(s: ClaimState, view: ClaimView): boolean {
  return view.retry === null && s.picks.length === view.slots.length && s.picks.every((p) => p !== null)
}

function stepWord(s: ClaimState, view: ClaimView, slot: number, dir: 1 | -1): ClaimState {
  const choices = view.slots[slot]?.choices ?? []
  if (choices.length === 0) return s
  const at = choices.findIndex((c) => c.word === s.picks[slot])
  const next = at < 0 ? (dir === 1 ? 0 : choices.length - 1) : (at + dir + choices.length) % choices.length
  return withPick(s, slot, choices[next].word)
}

function withPick(s: ClaimState, slot: number, word: string): ClaimState {
  return { ...s, picks: s.picks.map((p, i) => (i === slot ? word : p)), feedback: null }
}

function send(s: ClaimState, view: ClaimView): ClaimStep {
  if (!canSend(s, view)) return { state: s, effect: null }
  return { state: s, effect: { kind: 'submit', tier: view.tier, words: s.picks.filter((p): p is string => p !== null) } }
}

export function reduceClaim(prev: ClaimState, a: ClaimAction, view: ClaimView, cfg: ClaimPanelConfig, wrong: string): ClaimStep {
  const s = clampClaim(prev, view)
  const sendAt = view.slots.length
  switch (a.type) {
    case 'pick':
      if (view.slots[a.slot]?.choices.some((c) => c.word === a.word) !== true) return { state: s, effect: null }
      return { state: { ...withPick(s, a.slot, a.word), focus: a.slot }, effect: null }
    case 'focus':
      return { state: { ...s, focus: Math.min(Math.max(0, a.index), sendAt) }, effect: null }
    case 'send':
      return send({ ...s, focus: sendAt }, view)
    case 'result':
      return { state: { ...s, feedback: a.correct ? cfg.claim.sent : wrong }, effect: null }
    case 'nav':
      break
  }
  switch (a.input) {
    case 'back':
    case 'start':
      return { state: s, effect: { kind: 'close' } }
    case 'up':
      return { state: { ...s, focus: Math.max(0, s.focus - 1) }, effect: null }
    case 'down':
      return { state: { ...s, focus: Math.min(sendAt, s.focus + 1) }, effect: null }
    case 'left':
    case 'right':
      if (s.focus >= sendAt) return { state: s, effect: null }
      return { state: stepWord(s, view, s.focus, a.input === 'left' ? -1 : 1), effect: null }
    case 'activate':
      if (s.focus >= sendAt) return send(s, view)
      return { state: stepWord(s, view, s.focus, 1), effect: null }
  }
}
